// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductModel, VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { withCarouselProductContext } from "@west/App/Components/ProductCarousel/CarouselProductContext";
import updateCarouselProductsVariantSelection from "@west/App/Handlers/ProductCarousel/UpdateCarouselProductsVariantSelection";
import { CustomAppState } from "@west/App/Store/Reducers";
import ProductDetailsVariantButton from "@west/Overrides/Widgets/ProductDetails/ProductDetailsVariantButton";
import ProductDetailsVariantDropdown from "@west/Overrides/Widgets/ProductDetails/ProductDetailsVariantDropdown";
import ProductDetailsVariantSwatchDropdown from "@west/Overrides/Widgets/ProductDetails/ProductDetailsVariantSwatchDropdown";
import ProductDetailsVariantSwatchGrid from "@west/Overrides/Widgets/ProductDetails/ProductDetailsVariantSwatchGrid";
import ProductDetailsVariantSwatchList from "@west/Overrides/Widgets/ProductDetails/ProductDetailsVariantSwatchList";
import { mergeWithCss } from "@west/XDL/Utilities";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    widgetId: string;
    extendedStyles?: ProductCarouselVariantOptionsStyles;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps & HasProductContext) => {
    const product = ownProps.productContext.product;
    const parentProductId = product.properties.styleParentId;

    return {
        parentProductId,
        variantSelection:
            state.xProductCarouselVariantSelection.variantSelection?.[ownProps.widgetId]?.[parentProductId],
        variantTraits: product.variantTraits,
        variantChildren:
            state.xProductCarouselVariantChildren.variantChildren?.[ownProps.widgetId]?.[parentProductId] || [],
    };
};

const mapDispatchToProps = {
    updateCarouselProductsVariantSelection,
};

export interface ProductCarouselVariantOptionsStyles {
    wrapper?: InjectableCss;
    availableOptionsWrapper?: InjectableCss;
    availableOptionsLink?: LinkPresentationProps;
}

export const productCarouselVariantOptionsStyles: ProductCarouselVariantOptionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            row-gap: 12px;
        `,
    },
};

const ProductCarouselVariantOptions: React.FC<Props> = ({
    variantChildren,
    variantTraits,
    parentProductId,
    variantSelection,
    updateCarouselProductsVariantSelection,
    widgetId,
    productContext,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(productCarouselVariantOptionsStyles, extendedStyles));

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateCarouselProductsVariantSelection({
            needToLoadChildren: true,
            parentProductId,
            variantSelection: { [variantTraitId]: traitValueId, variantTraitId },
            carouselId: widgetId,
        });
    };

    if (!variantSelection || !variantTraits || variantTraits.length === 0 || !variantChildren) {
        return null;
    }

    const renderVariant = (variantTrait: VariantTraitModel) => {
        switch (variantTrait.displayType) {
            case VariantDisplayTypeValues.Button:
                return (
                    <ProductDetailsVariantButton
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                        showLabel={false}
                        showClear={false}
                    />
                );
            case VariantDisplayTypeValues.SwatchDropdown:
                return (
                    <ProductDetailsVariantSwatchDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                        showLabel={false}
                    />
                );
            case VariantDisplayTypeValues.SwatchGrid:
                return (
                    <ProductDetailsVariantSwatchGrid
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                        showLabel={false}
                        showClear={false}
                    />
                );
            case VariantDisplayTypeValues.SwatchList:
                return (
                    <ProductDetailsVariantSwatchList
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                        showLabel={false}
                        showClear={false}
                    />
                );
            default:
                return (
                    <ProductDetailsVariantDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                        labelPosition="inside"
                    />
                );
        }
    };

    const productDetailPath = productContext.product.canonicalUrl ?? productContext.productInfo.productDetailPath;
    // get number of traits in dropdownVariants
    const optionsAvailableCount =
        variantTraits.find(variantTrait => variantTrait.displayType === "Dropdown")?.traitValues?.length || 0;

    // Order traitValues
    const sortedVariantTraits = variantTraits.map(({ traitValues, ...rest }) => ({
        ...rest,
        traitValues: traitValues?.slice().sort((a, b) => a.sortOrder - b.sortOrder),
    }));
    return (
        <StyledWrapper {...styles.wrapper}>
            {optionsAvailableCount > 0 && (
                <StyledWrapper {...styles.availableOptionsWrapper}>
                    <Link
                        href={productDetailPath}
                        {...styles.availableOptionsLink}
                    >{`${optionsAvailableCount} ${translate("Options Available")}`}</Link>
                </StyledWrapper>
            )}
            {sortedVariantTraits.map(
                variantTrait => !!variantTrait.traitValues?.length && renderVariant(variantTrait as VariantTraitModel),
            )}
        </StyledWrapper>
    );
};

export default withCarouselProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductCarouselVariantOptions));
